// Task #3007 — single source of truth for which top-level card the
// AccountPayoutsPage renders for a given resolver decision.
//
// Mirrors the policy-reason vocabulary defined server-side in
// `artifacts/api-server/src/lib/payout-policy.ts`. The server is
// authoritative — this helper only consumes the decision object the
// page fetched from /api/payout-policy. Keep the branch IDs in sync
// with the `data-testid`s rendered by AccountPayoutsPage so tests
// can assert which card is showing.
//
// NB: We accept a minimal shape (`tier | payoutMethod | reason`) so
// callers and tests don't have to construct a full PayoutPolicyDecision.

export type PayoutsPageBranch =
  // Tier 0 OR resolver returned `no_rail_available` at any tier — host
  // has no automatic rail and no Stripe step to click; route to support.
  | "no_rail_support"
  // Tier 1, host hasn't onboarded yet → "Set up Stripe Connect" CTA.
  | "tier1_setup_missing"
  // Tier 1 (or Tier 2 fallback) → "Re-verify with Stripe" CTA.
  | "tier1_reverify"
  // Tier 4 manual-wire → read-only summary card, no rail picker.
  | "tier4_manual"
  // Tier 2 / 3 with at least one automatic rail → rail picker.
  | "picker";

export interface PayoutsBranchInput {
  tier: number;
  payoutMethod: "connect" | "payout_link" | "manual" | "awaiting_setup";
  reason?: string | null;
}

export function payoutsPageBranch(d: PayoutsBranchInput): PayoutsPageBranch {
  // The reviewer-flagged misroute: `no_rail_available` can arrive
  // with payoutMethod="awaiting_setup" at ANY tier (host-override
  // narrowing the rail set, or the capability cache marking the
  // country offline). Those hosts can't fix it themselves — Stripe
  // re-verify won't help — so they get the support card regardless
  // of tier. This check MUST run before the awaiting_setup branch
  // below or Tier-1 reverify copy will mask a no-rail incident.
  if (d.reason === "no_rail_available" || d.tier === 0) return "no_rail_support";
  // Payout links are retired from all active markets (Tier 1 / Tier 4 only).
  // Any host whose policy still resolves to payout_link (legacy Tier 3
  // record not yet migrated) cannot manage links from the frontend — route
  // them to the support card so they aren't silently stranded.
  if (d.payoutMethod === "payout_link") return "no_rail_support";
  if (d.payoutMethod === "awaiting_setup") {
    if (d.reason === "tier1_connect_required_but_missing") return "tier1_setup_missing";
    // Any other awaiting_setup state means the Stripe account exists
    // but isn't healthy — the actionable fix is re-verification.
    return "tier1_reverify";
  }
  if (d.tier === 4) return "tier4_manual";
  return "picker";
}

/**
 * Task #3007 (code-review follow-up #2) — secondary signal layered on
 * top of `payoutsPageBranch`. True when the resolver dropped a Tier 2
 * host onto the payout-link rail because their Connect account is
 * unhealthy. The post-trip email + held-funds nudge both promise a
 * Stripe re-verify CTA for this case (intent=connect_reverify); the
 * Payouts page must honor that promise by rendering a re-verify banner
 * ABOVE the rail picker. We don't replace the picker — Tier 2 hosts
 * still have a working payout-link rail and can receive funds today
 * — we just need to surface the Connect fix so the email's CTA isn't
 * a dead end.
 */
export function payoutsPageShowsReverifyBanner(d: PayoutsBranchInput): boolean {
  return (
    d.reason === "connect_unhealthy_falling_back_to_payout_link" &&
    d.payoutMethod === "payout_link"
  );
}

/** Minimal shape of the /api/payments/connect/status response that the
 *  payouts page needs to decide whether the "direct deposit" rail is
 *  selectable. Mirrors `BookingsApi.getStripeConnectStatus`. */
export interface ConnectStatusForUsable {
  connected?: boolean;
  payoutsEnabled?: boolean;
  /** True when the account record exists but onboarding is incomplete.
   *  Treated as not-usable (same as `connected === false`). */
  needsOnboarding?: boolean;
}

/**
 * Task #4287 — frontend mirror of the server-side `isConnectAccountUsable`
 * guard. A host may only make "direct deposit" (Stripe Connect) their
 * preferred payout method when they have a usable connected account:
 * connected, onboarding complete, and payouts enabled. Until then the rail
 * starts onboarding instead of saving a non-functional preference.
 *
 * Fails closed: a missing status, any unset flag, or `needsOnboarding`
 * leaves the rail "not usable" so the page routes the host into onboarding
 * rather than persisting a preference the server would reject with 409.
 */
export function isConnectRailUsable(status: ConnectStatusForUsable | null | undefined): boolean {
  return (
    !!status &&
    status.connected === true &&
    status.needsOnboarding !== true &&
    status.payoutsEnabled === true
  );
}
